/**
 * Sign-out after inactivity, browser side.
 *
 * The backend refuses the API token of an account that made no user-driven
 * request for SESSION_INACTIVITY_HOURS (userapp/session_expiry.py) and
 * answers 401. The response interceptor installed here ends the session in
 * the browser on any 401 from the backend while a token is stored: the
 * stored credentials are cleared, the reason is kept for the login page and
 * the app goes to /login.
 *
 * Background polling (the System page) carries the X-Oraclase-Background
 * header so the backend does not count it as activity.
 */

export const BACKGROUND_HEADER = "X-Oraclase-Background";
export const SESSION_ENDED_KEY = "sessionEndedMessage";
export const SESSION_ENDED_MESSAGE = "Your session has ended. Please sign in again.";

/** Axios config for a request that must not count as user activity. */
export function backgroundRequest(config = {}) {
  return { ...config, headers: { ...(config.headers || {}), [BACKGROUND_HEADER]: "1" } };
}

const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;

/** True for a request to this app's backend (relative URL or under baseURL);
 * the local laser agent and other hosts are not the backend's business. */
export function isBackendRequest(config, baseURL) {
  const url = config?.url || "";
  if (!SCHEME.test(url)) return true;
  return Boolean(baseURL) && url.startsWith(baseURL);
}

const isLoginRequest = (config) => (config?.url || "").replace(/^\//, "").startsWith("api/login");

/**
 * End the session in the browser when ``error`` is a 401 from the backend
 * while a token is stored. Returns true when it did. The reason shown on the
 * login page is the backend's message for an inactivity sign-out, otherwise
 * a generic one.
 */
export function handleUnauthorized(error, { baseURL, redirect }) {
  const config = error?.config;
  if (error?.response?.status !== 401 || !config) return false;
  if (!isBackendRequest(config, baseURL) || isLoginRequest(config)) return false;
  if (!localStorage.getItem("token")) return false;
  const detail = error.response?.data?.detail;
  const message =
    typeof detail === "string" && /inactivity/i.test(detail) ? detail : SESSION_ENDED_MESSAGE;
  localStorage.removeItem("token");
  localStorage.removeItem("username");
  localStorage.removeItem("userId");
  try {
    sessionStorage.setItem(SESSION_ENDED_KEY, message);
  } catch (err) {
    // Session storage can be unavailable; the login page then shows nothing.
  }
  redirect("/login");
  return true;
}

/** The reason the last session ended, shown once on the login page. */
export function takeSessionEndedMessage() {
  try {
    const message = sessionStorage.getItem(SESSION_ENDED_KEY);
    if (message) sessionStorage.removeItem(SESSION_ENDED_KEY);
    return message || null;
  } catch (err) {
    return null;
  }
}

/**
 * Register the response interceptor on ``axiosInstance``. Several requests
 * can fail at once when a session ends; the page is left only once.
 */
export function installSessionExpiryInterceptor(
  axiosInstance,
  redirect = (path) => window.location.assign(path)
) {
  let leaving = false;
  const leaveOnce = (path) => {
    if (leaving) return;
    leaving = true;
    redirect(path);
  };
  return axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const ended = handleUnauthorized(error, {
        baseURL: axiosInstance.defaults?.baseURL,
        redirect: leaveOnce,
      });
      if (ended && axiosInstance.defaults?.headers?.common) {
        delete axiosInstance.defaults.headers.common.Authorization;
      }
      return Promise.reject(error);
    }
  );
}
